<template>
  <v-main>
    <v-container fluid>
      <v-row justify="center">
        <v-col cols="12" md="8">
          <v-card elevation="6" rounded="xxl">
            <v-card-title class="headline primary--text">
              <v-icon class="mr-2">mdi-briefcase-plus</v-icon>
              Add Work History
            </v-card-title>

            <v-card-text>
              <v-form ref="form" v-model="valid" lazy-validation>
                <v-row>
                  <!-- Employee Selection -->
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="form.user_id"
                      :items="users"
                      item-text="name"
                      item-value="id"
                      label="Employee *"
                      :rules="[rules.required]"
                      solo
                      rounded
                      dense
                      required
                      :loading="loadingUsers"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>mdi-account</v-icon>
                      </template>
                    </v-select>
                  </v-col>

                  <!-- Organization Name -->
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.organization_name"
                      label="Organization Name"
                      solo
                      rounded
                      dense
                      :rules="[rules.maxLength(255)]"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>mdi-domain</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>

                  <!-- Job Title -->
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.job_title"
                      label="Job Title"
                      solo
                      rounded
                      dense
                      :rules="[rules.maxLength(255)]"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>mdi-briefcase</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>

                  <!-- City -->
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.city"
                      label="City"
                      solo
                      rounded
                      dense
                      :rules="[rules.maxLength(255)]"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>mdi-map-marker</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>

                  <!-- Start Date -->
                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="startDateMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="form.start_date"
                          label="Start Date"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          solo
                          rounded
                          dense
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="form.start_date"
                        @input="startDateMenu = false"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>

                  <!-- End Date -->
                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="endDateMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="form.end_date"
                          label="End Date (Leave empty for current position)"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          solo
                          rounded
                          dense
                          clearable
                          v-bind="attrs"
                          v-on="on"
                          :rules="[rules.endDateAfterStart]"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="form.end_date"
                        @input="endDateMenu = false"
                        :min="form.start_date"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>

                  <!-- Address -->
                  <v-col cols="12">
                    <v-textarea
                      v-model="form.address"
                      label="Address"
                      solo
                      rounded
                      dense
                      rows="3"
                      auto-grow
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>mdi-map</v-icon>
                      </template>
                    </v-textarea>
                  </v-col>

                  <!-- Description -->
                  <v-col cols="12">
                    <v-textarea
                      v-model="form.description"
                      label="Job Description & Responsibilities"
                      solo
                      rounded
                      dense
                      rows="4"
                      auto-grow
                      counter="1000"
                      :rules="[rules.maxLength(1000)]"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>mdi-text</v-icon>
                      </template>
                    </v-textarea>
                  </v-col>

                  <!-- Status -->
                  <v-col cols="12" md="6">
                    <v-switch
                      v-model="form.is_active"
                      label="Active Status"
                      color="success"
                      inset
                    ></v-switch>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>

            <v-card-actions class="px-6 pb-6">
              <v-btn
                color="grey"
                text
                @click="goBack"
                :disabled="saving"
              >
                <v-icon left>mdi-arrow-left</v-icon>
                Cancel
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                @click="saveWorkHistory"
                :loading="saving"
                :disabled="!valid"
              >
                <v-icon left>mdi-content-save</v-icon>
                Save Work History
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "WorkHistoryCreate",
  data() {
    return {
      valid: false,
      saving: false,
      loadingUsers: false,
      startDateMenu: false,
      endDateMenu: false,
      users: [],
      
      // Form data
      form: {
        user_id: null,
        organization_name: "",
        job_title: "",
        city: "",
        address: "",
        start_date: null,
        end_date: null,
        description: "",
        is_active: true,
      },
      
      // Validation rules
      rules: {
        required: value => !!value || "This field is required",
        maxLength: max => value => !value || value.length <= max || `Maximum ${max} characters allowed`,
        endDateAfterStart: value => {
          if (!value || !this.form.start_date) return true;
          return new Date(value) >= new Date(this.form.start_date) || "End date must be after start date";
        },
      },
    };
  },

  mounted() {
    this.getUsers();
    this.checkQueryParams();
  },

  methods: {
    async getUsers() {
      this.loadingUsers = true;
      try {
        const response = await axios.get("/users", {
          params: { rowsPerPage: 1000 }
        });
        
        this.users = response.data.data.map(user => ({
          id: user.id,
          name: `${user.first_name} ${user.last_name}`.trim() || user.email,
        }));
      } catch (error) {
        console.error("Error fetching users:", error);
        this.$toast.error("Failed to load users");
      } finally {
        this.loadingUsers = false;
      }
    },

    checkQueryParams() {
      // Pre-select user if user_id is provided in query params
      if (this.$route.query.user_id) {
        this.form.user_id = parseInt(this.$route.query.user_id);
      }
    },

    async saveWorkHistory() {
      if (!this.$refs.form.validate()) {
        this.$toast.error("Please fix validation errors");
        return;
      }

      this.saving = true;
      try {
        // Prepare data
        const data = { ...this.form };
        
        // Remove empty strings
        Object.keys(data).forEach(key => {
          if (data[key] === "") {
            data[key] = null;
          }
        });

        await axios.post("/work_histories", data);
        
        this.$toast.success("Work history created successfully");
        this.$router.push("/work_histories");
      } catch (error) {
        console.error("Error creating work history:", error);
        
        if (error.response && error.response.data && error.response.data.errors) {
          // Display validation errors
          const errors = error.response.data.errors;
          Object.keys(errors).forEach(key => {
            this.$toast.error(errors[key][0]);
          });
        } else {
          this.$toast.error("Failed to create work history");
        }
      } finally {
        this.saving = false;
      }
    },

    goBack() {
      this.$router.push("/work_histories");
    },

    resetForm() {
      this.$refs.form.reset();
      this.form = {
        user_id: null,
        organization_name: "",
        job_title: "",
        city: "",
        address: "",
        start_date: null,
        end_date: null,
        description: "",
        is_active: true,
      };
    },
  },
};
</script>

<style scoped>
.v-card {
  border-radius: 20px !important;
}

.v-text-field--outlined >>> fieldset {
  border-radius: 25px;
}

.v-textarea >>> .v-input__control .v-input__slot {
  border-radius: 25px;
}
</style>